import { Card, CardActions, CardContent, Portal } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useCallback, useEffect, useMemo } from 'react'
import {
  downloadSupportBundle,
  getSupportBundleStatus,
} from 'app/plugins/openstack/components/infrastructure/actions'
import { BundleStatusResponse } from 'app/plugins/openstack/components/infrastructure/hosts/generate-support-bundle-modal/model'
import BundleRow, {
  BUNDLE_STATUS,
} from 'app/plugins/openstack/components/infrastructure/hosts/support-bundle-popover/BundleRow'
import PopoverHeader from 'app/plugins/openstack/components/infrastructure/hosts/support-bundle-popover/PopoverHeader'
import PollingData, { POLLING_INTERVALS } from 'core/components/PollingData'
import { BundleStatus, useSupportBundleProgress } from 'core/context/supportBundleProgressContext'
import Checkbox from 'core/elements/input/Checkbox'

const POLLING_INTERVAL = POLLING_INTERVALS.THIRTY_SECONDS

// Main Component
const SupportBundleProgressPopover: React.FC = () => {
  const classes = useStyles()
  const {
    bundleStates,
    bundles,
    updateBundleStatus,
    setBundleDownloading,
    setBundleDownloadError,
    setAutoDownloadTriggered,
    removeBundle,
    closeAllBundles,
  } = useSupportBundleProgress()

  const [isExpanded, setIsExpanded] = React.useState(true)
  const [autoDownload, setAutoDownload] = React.useState(true)
  const [loading, setLoading] = React.useState(false)

  const bundleList = useMemo(() => Object.values(bundleStates), [bundleStates])

  // Track bundles that need auto-download
  const bundlesToAutoDownload = useMemo(() => {
    if (!autoDownload) return []
    return Object.entries(bundleStates).filter(
      ([, bundleState]) =>
        (bundleState.overallStatus === BUNDLE_STATUS.COMPLETED ||
          bundleState.overallStatus === BUNDLE_STATUS.PARTIAL_SUCCESS) &&
        !bundleState.autoDownloadTriggered &&
        !bundleState.downloading,
    )
  }, [bundleStates, autoDownload])

  // Determine if polling should pause
  const shouldPausePolling = useMemo(() => {
    if (bundles.length === 0) {
      return true
    }

    const activeBundles = bundles.filter((bundle) => {
      const state = bundleStates[bundle.bundleId]
      if (!state) return true
      return (
        state.overallStatus === BUNDLE_STATUS.PENDING ||
        state.overallStatus === BUNDLE_STATUS.IN_PROGRESS
      )
    })

    return activeBundles.length === 0
  }, [bundles, bundleStates])

  const hasActiveBundles = useMemo(() => {
    return Object.values(bundleStates).some(
      (state) =>
        state.overallStatus === BUNDLE_STATUS.PENDING ||
        state.overallStatus === BUNDLE_STATUS.IN_PROGRESS,
    )
  }, [bundleStates])

  // Handlers
  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  const handleClose = useCallback((bundleId: string) => {
    removeBundle(bundleId)
  }, [])

  const handleCloseAll = useCallback(() => {
    closeAllBundles()
  }, [closeAllBundles])

  // Poll for all bundle statuses - only poll active bundles
  const pollAllStatuses = useCallback(async () => {
    if (bundles.length === 0) return

    // Filter to only poll bundles that are still active (pending or in_progress)
    const activeBundles = bundles.filter((bundle) => {
      const state = bundleStates[bundle.bundleId]
      if (!state) return true // If state doesn't exist, consider it active
      return (
        state.overallStatus === BUNDLE_STATUS.PENDING ||
        state.overallStatus === BUNDLE_STATUS.IN_PROGRESS
      )
    })

    if (activeBundles.length === 0) {
      return
    }

    setLoading(true)
    try {
      const statusPromises = activeBundles.map(async (bundle) => {
        try {
          const response = (await getSupportBundleStatus({
            bundleId: bundle.bundleId,
          })) as BundleStatusResponse

          return {
            bundleId: bundle.bundleId,
            data: response,
          }
        } catch (err) {
          const errorMessage =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            (err instanceof Error ? err.message : 'Failed to fetch bundle status')
          return {
            bundleId: bundle.bundleId,
            data: {
              bundleId: bundle.bundleId,
              overallStatus: BUNDLE_STATUS.FAILED as BundleStatus,
              error: errorMessage,
            },
          }
        }
      })

      const results = await Promise.all(statusPromises)

      results.forEach(({ bundleId, data }) => {
        updateBundleStatus(bundleId, data)
      })
    } catch (err) {
      throw err
    } finally {
      setLoading(false)
    }
  }, [bundles, bundleStates, updateBundleStatus])

  // Download bundle
  const downloadBundle = useCallback(
    async (bundleId: string) => {
      const bundleState = bundleStates[bundleId]
      if (!bundleState) return

      setBundleDownloading(bundleId, true)

      try {
        const blob = await downloadSupportBundle({ bundleId })
        const filename = `support_bundle_${bundleId}.zip`

        // Create download link and trigger download
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        setBundleDownloading(bundleId, false)
      } catch (err) {
        const errorMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.error?.message ||
          (err instanceof Error ? err.message : 'Failed to download bundle')

        setBundleDownloading(bundleId, false)
        setBundleDownloadError(bundleId, errorMessage)
      }
    },
    [bundleStates, setBundleDownloading, setBundleDownloadError],
  )

  // Reset autoDownload to true when popover is closed
  useEffect(() => {
    if (Object.keys(bundleStates)?.length === 0) {
      setAutoDownload(true)
    }
  }, [bundleStates])

  // Trigger initial poll when bundles are added
  useEffect(() => {
    if (bundles.length > 0) {
      pollAllStatuses()
    }
    return undefined
  }, [bundles.length])

  // Auto-download all ready bundles when autoDownload is enabled
  useEffect(() => {
    if (bundlesToAutoDownload.length === 0) return

    bundlesToAutoDownload.forEach(([bundleId]) => {
      setAutoDownloadTriggered(bundleId)
      downloadBundle(bundleId)
    })
  }, [bundlesToAutoDownload])

  if (Object.keys(bundleStates).length === 0) {
    return null
  }

  return (
    <Portal>
      <PollingData
        hidden
        loading={loading}
        pause={shouldPausePolling}
        refreshDuration={POLLING_INTERVAL}
        onReload={pollAllStatuses}
      />
      <Card className={classes.card}>
        <PopoverHeader
          isExpanded={isExpanded}
          onToggleExpand={handleToggleExpand}
          onClose={handleCloseAll}
          bundleCount={Object.keys(bundleStates)?.length}
          hasActiveBundles={hasActiveBundles}
        />
        {isExpanded && (
          <>
            <CardContent className={classes.content}>
              {bundleList?.map((bundle, index) => (
                <BundleRow
                  key={bundle?.bundleId}
                  index={index}
                  bundle={bundle}
                  onDownload={downloadBundle}
                  onClose={handleClose}
                  bundlesCount={bundleList?.length}
                />
              ))}
            </CardContent>
            <CardActions className={classes.cardActions}>
              <div className={classes.checkboxContainer}>
                <Checkbox
                  checked={autoDownload}
                  onChange={setAutoDownload}
                  label="Start download automatically"
                />
              </div>
            </CardActions>
          </>
        )}
      </Card>
    </Portal>
  )
}

export default SupportBundleProgressPopover

const useStyles = makeStyles((theme) => ({
  card: {
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    width: 480,
    maxHeight: 600,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    overflow: 'hidden',
    zIndex: 990,
    display: 'flex',
    flexDirection: 'column',
  },
  cardActions: {
    backgroundColor: theme.palette.grey[100],
  },
  content: {
    padding: theme.spacing(2),
    overflowY: 'auto',
    flex: 1,
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
}))
